import React, { useState, useEffect } from "react";
import { X, FileText, Search, Loader, AlertCircle } from "lucide-react";
import { supabase } from "../../lib/supabase";

interface DownloadSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string, fileName: string) => void;
}

interface Upload {
  id: string;
  name: string;
  url: string;
  size: number;
  file_type: string;
  description?: string;
  created_at: string;
}

export default function DownloadSelectorModal({
  isOpen,
  onClose,
  onSelect,
}: DownloadSelectorModalProps) {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUpload, setSelectedUpload] = useState<Upload | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchUploads();
    } else {
      setSelectedUpload(null);
      setSearchQuery("");
    }
  }, [isOpen]);

  const fetchUploads = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("uploads")
        .select("*")
        .eq("deleted", false)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setUploads(data || []);
    } catch (err) {
      console.error("Error fetching uploads:", err);
      setError("Failed to load uploads");
    } finally {
      setLoading(false);
    }
  };

  const filteredUploads = uploads.filter((upload) => {
    if (!searchQuery) return true;
    return (
      upload.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (upload.description &&
        upload.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      upload.file_type.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleSelect = (upload: Upload) => {
    setSelectedUpload(upload);
  };

  const handleConfirm = () => {
    if (selectedUpload) {
      onSelect(selectedUpload.url, selectedUpload.name);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Select Downloadable File
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <Loader className="h-8 w-8 text-[#F98B3D] animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          ) : filteredUploads.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No files found</p>
              <p className="text-sm text-gray-500 mt-2">
                Upload files in the Admin &gt; Uploads section
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredUploads.map((upload) => (
                <button
                  key={upload.id}
                  onClick={() => handleSelect(upload)}
                  className={`flex items-center p-4 border rounded-lg text-left ${
                    selectedUpload?.id === upload.id
                      ? "border-[#F98B3D] bg-orange-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <FileText
                    className={`h-10 w-10 mr-4 ${
                      selectedUpload?.id === upload.id
                        ? "text-[#F98B3D]"
                        : "text-gray-400"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-medium truncate ${
                        selectedUpload?.id === upload.id
                          ? "text-[#F98B3D]"
                          : "text-gray-900"
                      }`}
                    >
                      {upload.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(upload.size)} •{" "}
                      {upload.file_type.split("/")[1].toUpperCase()} •{" "}
                      {new Date(upload.created_at).toLocaleDateString()}
                    </p>
                    {upload.description && (
                      <p className="text-xs text-gray-600 mt-1 truncate">
                        {upload.description}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="mr-3 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedUpload}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              !selectedUpload
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#F98B3D] text-white hover:bg-[#e07a2c]"
            }`}
          >
            Select File
          </button>
        </div>
      </div>
    </div>
  );
}
